import type {IntlShape} from 'react-intl'
import type {Notification} from '../redux/reducers/notifications/types'
import {formatBillingMoney} from '../components/billing/billingDisplayHelpers'

export const BILLING_UNPAID_NOTIFICATION_TYPE = 'BILLING_UNPAID'

export type BillingUnpaidNotificationPayload = {
  studentId?: string
  titleKey?: string
  messageKey?: string
  totalUnpaid?: number
}

/** Parses `additionalDataID` JSON from billing reminder in-app notifications. */
export function parseBillingUnpaidNotificationPayload(
  notification: Notification & {additionalDataID?: string}
): BillingUnpaidNotificationPayload | null {
  const raw = notification.additionalDataID
  if (!raw || typeof raw !== 'string') return null
  try {
    const parsed = JSON.parse(raw) as BillingUnpaidNotificationPayload
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

export function isBillingUnpaidNotificationType(type: string | undefined): boolean {
  return type === BILLING_UNPAID_NOTIFICATION_TYPE
}

export function formatBillingUnpaidNotificationCopy(
  intl: IntlShape,
  notification: Notification & {additionalDataID?: string}
): {main: string; sub: string} {
  const payload = parseBillingUnpaidNotificationPayload(notification)
  const titleId = payload?.titleKey ?? 'billing.unpaid.title'
  const messageId = payload?.messageKey ?? 'billing.unpaid.message'
  const total =
    payload?.totalUnpaid != null && Number.isFinite(payload.totalUnpaid)
      ? formatBillingMoney(payload.totalUnpaid)
      : undefined

  return {
    main: intl.formatMessage({
      id: titleId,
      defaultMessage: 'School payment reminder',
    }),
    sub: intl.formatMessage(
      {
        id: messageId,
        defaultMessage: 'You have unpaid school charges. Remaining: {total}.',
      },
      {total: total ?? '—'}
    ),
  }
}

export function formatBillingUnpaidPushTitle(): string {
  return 'School payment reminder'
}

export function formatBillingUnpaidPushBody(
  notification: Notification & {additionalDataID?: string}
): string {
  const payload = parseBillingUnpaidNotificationPayload(notification)
  if (payload?.totalUnpaid != null && Number.isFinite(payload.totalUnpaid)) {
    return `Remaining: ${formatBillingMoney(payload.totalUnpaid)}`
  }
  return 'You have unpaid school charges.'
}
